import React from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Building,
  Users,
  Key,
  ArrowRight,
  BarChart3,
  Lock,
  Smartphone,
  Copy,
  Check
} from 'lucide-react'
import { useState } from 'react'
import type { User } from '@/lib/types'
import { RequestAccessModal } from '@/pages/modals/RequestAccessModal'
import { useToast } from '@/hooks/use-toast'

interface ProviderDashboardStats {
  totalUsers: number
  activeTokens: number
  verificationStatus: string
  institutionScore: number
  recentActivity: any[]
}

interface ProviderDashboardProps {
  user: User
  stats: ProviderDashboardStats | null
}

const ProviderDashboardContent: React.FC<ProviderDashboardProps> = ({ user, stats }) => {
  const providerProfile = user.profile as any
  const isVerified = user.profile.isVerified
  const [showAccessModal, setShowAccessModal] = useState(false)
  const [copiedId, setCopiedId] = useState(false)
  const { toast } = useToast()

  const handleCopyProviderId = async () => {
    if (providerProfile.providerId) {
      try {
        await navigator.clipboard.writeText(providerProfile.providerId)
        setCopiedId(true)
        toast({
          title: "Provider ID Copied",
          description: "Your Provider ID has been copied to clipboard.",
        })
        setTimeout(() => setCopiedId(false), 2000)
      } catch (error) {
        toast({
          title: "Copy Failed",
          description: "Could not copy Provider ID. Please try again.",
          variant: "destructive",
        })
      }
    }
  }

  return (
    <div className="space-y-8">
      {/* Hero Header - Minimalist */}
      <div className="bg-white rounded-3xl shadow-sm p-8 border border-slate-100">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-6">
            <div className="h-20 w-20 rounded-2xl bg-[#0F172A] flex items-center justify-center shadow-lg shadow-blue-900/20">
              <Building className="h-10 w-10 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#D64463] mb-1">Organization Overview</p>
              <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">
                {providerProfile.institutionName || 'Healthcare Institution'}
              </h1>
              <p className="text-slate-500 font-medium text-lg mt-1">
                {providerProfile.institutionType || 'Medical Center'}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 min-w-[200px]">
            {/* Provider ID Display */}
            {providerProfile.providerId && (
              <div className="bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 place-self-start lg:place-self-end">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Provider ID</p>
                <div className="flex items-center gap-2">
                  <code className="text-lg font-mono font-bold text-[#0F172A]">{providerProfile.providerId}</code>
                  <button
                    onClick={handleCopyProviderId}
                    className="text-slate-400 hover:text-[#D64463] transition-colors p-1 rounded-md hover:bg-slate-100"
                    title="Copy Provider ID"
                  >
                    {copiedId ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            )}
            <Badge className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wide place-self-start lg:place-self-end ${isVerified
              ? 'bg-emerald-50 text-emerald-600 border-0'
              : 'bg-amber-50 text-amber-600 border-0'
              }`}>
              {isVerified ? '✓ Verified Organization' : '○ Verification Pending'}
            </Badge>
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-[#D64463] hover:bg-[#b0304c] text-white shadow-md shadow-pink-500/20 rounded-xl h-11 transition-all"
                onClick={() => setShowAccessModal(true)}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Connect App
              </Button>
              <Button className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white shadow-md shadow-slate-900/20 rounded-xl h-11 transition-all">
                Check Compliance
              </Button>
            </div>
          </div>
        </div>

        <RequestAccessModal isOpen={showAccessModal} onClose={() => setShowAccessModal(false)} />

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-2xl bg-slate-50 p-5 border border-slate-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Status</p>
            <p className="text-sm font-medium text-slate-700 leading-relaxed">{isVerified ? 'Your organization is fully verified.' : 'Complete verification to unlock advanced analytics.'}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-5 border border-slate-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Support</p>
            <p className="text-sm font-medium text-slate-700 leading-relaxed">Need help? Schedule an onboarding call in minutes.</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-5 border border-slate-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Next Step</p>
            <p className="text-sm font-medium text-slate-700 leading-relaxed">Invite your care team and start sharing secure records.</p>
          </div>
        </div>
      </div>

      {/* Modern Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Patients */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
          <div className="flex items-center justify-between mb-6">
            <div className="h-12 w-12 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:bg-[#0F172A] transition-colors duration-300">
              <Users className="h-6 w-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
            </div>
            {/* Growth badge removed */}
          </div>
          <p className="text-4xl font-extrabold text-slate-900 mb-1">{stats?.totalUsers || 0}</p>
          <p className="text-sm font-medium text-slate-400">Total Patients</p>
        </div>

        {/* Active Tokens */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
          <div className="flex items-center justify-between mb-6">
            <div className="h-12 w-12 rounded-2xl bg-pink-50 flex items-center justify-center group-hover:bg-[#D64463] transition-colors duration-300">
              <Key className="h-6 w-6 text-[#D64463] group-hover:text-white transition-colors duration-300" />
            </div>
            {/* Growth badge removed */}
          </div>
          <p className="text-4xl font-extrabold text-slate-900 mb-1">{stats?.activeTokens || 0}</p>
          <p className="text-sm font-medium text-slate-400">Active Tokens</p>
        </div>

        {/* Compliance Status */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
          <div className="flex items-center justify-between mb-6">
            <div className="h-12 w-12 rounded-2xl bg-purple-50 flex items-center justify-center group-hover:bg-purple-600 transition-colors duration-300">
              <Lock className="h-6 w-6 text-purple-600 group-hover:text-white transition-colors duration-300" />
            </div>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${isVerified ? 'text-emerald-600 bg-emerald-50' : 'text-amber-600 bg-amber-50'}`}>
              {isVerified ? 'ACTIVE' : 'PENDING'}
            </span>
          </div>
          <p className="text-4xl font-extrabold text-slate-900 mb-1">{stats?.institutionScore || 0}%</p>
          <p className="text-sm font-medium text-slate-400">Compliance Score</p>
        </div>
      </div>

      {/* Quick Actions - Aesthetic Cards */}
      <h2 className="text-xl font-bold text-slate-900">Quick Actions</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: Key, title: "Generate Token", desc: "Create secure access tokens", action: "Generate", color: "bg-[#0F172A] hover:bg-[#1E293B]" },
          { icon: Users, title: "Manage Patients", desc: "View and organize patients", action: "View All", color: "bg-[#D64463] hover:bg-[#b0304c]" },
          { icon: BarChart3, title: "Analytics", desc: "View system analytics", action: "Explore", color: "bg-indigo-600 hover:bg-indigo-700" }
        ].map((item, idx) => (
          <div key={idx} className="bg-white border border-slate-100 rounded-3xl p-6 hover:shadow-lg transition-all duration-300 group flex flex-col items-start relative overflow-hidden">

            <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full blur-3xl -mr-16 -mt-16 transition-opacity opacity-50 group-hover:opacity-100"></div>

            <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-6 shadow-sm ${idx === 1 ? 'bg-pink-50 text-[#D64463]' : idx === 2 ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-700'}`}>
              <item.icon className="h-6 w-6" />
            </div>

            <h3 className="font-bold text-slate-900 text-lg mb-1">{item.title}</h3>
            <p className="text-sm font-medium text-slate-400 mb-8">{item.desc}</p>

            <Button className={`w-full mt-auto rounded-xl h-11 shadow-md text-white border-0 transition-transform active:scale-95 ${item.color}`}>
              {item.action}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ProviderDashboardContent